import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, Undo2, Redo2, RotateCw, Copy, Maximize2, Tag, Wand2, Boxes, SlidersHorizontal, Check, Loader2, X, MoreHorizontal, Menu, Box, Square, Maximize, Minimize, Magnet, RefreshCw, Save, ArrowUpRight, PanelBottom, HeartPulse, Grid3x3, Map, Move, MapPin, Camera } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { TooltipProvider } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuCheckboxItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuShortcut } from "@/components/ui/dropdown-menu";
import { LoadingState, ErrorState } from "@/components/common/States";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { num, fmtDateTime } from "@/lib/format";
import { useWorkspace, workspace } from "@/lib/workspace";
import { useRigEditor } from "@/components/rig/useRigEditor";
import { RigScene, SCENE_BG } from "@/components/rig/RigScene";
import { ModuleLibrary } from "@/components/rig/ModuleLibrary";
import { RigInspector } from "@/components/rig/RigInspector";
import { RigGeneratePopover } from "@/components/rig/RigGenerateDialog";
import { RigDrawer, DRAWER_TABS, DeliveryPanel, healthTone, healthText } from "@/components/rig/RigDrawers";
import { Glass, Seg, ToolButton, Dot, LEVEL_DOT } from "@/components/rig/RigControls";
import { SitePrompt, SiteLayerControl, SiteAttribution, SegIconLabel } from "@/components/rig/SitePanel";
import { fmtLatLng } from "@/components/rig/siteGeo";
import { DELIVERY_COLORS, DELIVERY_LABELS, DELIVERY_ORDER, mnok } from "@/components/rig/rigGeometry";

const DRAWER_H = 300;

/* ---------------- small pieces ---------------- */

const SaveStatus = ({ ed, className }) => {
  if (!ed.canEdit) return <span className={cn("text-[11.5px] text-muted-foreground", className)} data-testid="rig-save-status">Lesetilgang</span>;
  if (ed.saving) return <span className={cn("inline-flex items-center gap-1.5 text-[11.5px] text-muted-foreground", className)} data-testid="rig-save-status"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Lagrer…</span>;
  if (ed.dirty) return <button type="button" onClick={ed.save} title="Lagre nå (Ctrl+S)" className={cn("inline-flex h-7 items-center gap-1.5 rounded-md px-1.5 text-[11.5px] text-amber-700 hover:bg-muted", className)} data-testid="rig-save-status"><span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden /> Ulagret</button>;
  return <span className={cn("inline-flex items-center gap-1.5 text-[11.5px] text-muted-foreground", className)} title={ed.savedAt ? `Lagret ${fmtDateTime(ed.savedAt)}` : undefined} data-testid="rig-save-status"><Check className="h-3.5 w-3.5" /> Lagret{ed.version ? <span className="font-mono">v{ed.version}</span> : null}</span>;
};

const Stat = ({ label, value, hint, tone, onClick, testId }) => {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp type={onClick ? "button" : undefined} onClick={onClick} data-testid={testId} className={cn("inline-flex h-7 shrink-0 items-baseline gap-1.5 rounded-md px-2 text-left", onClick && "transition-colors duration-150 hover:bg-muted/70 focus-visible:outline-none focus-visible:bg-muted/70")}>
      <span className="text-[11.5px] text-muted-foreground">{label}</span>
      <span className={cn("text-[12.5px] font-medium tabular", tone === "warn" && "text-amber-700")}>{value}</span>
      {hint && <span className="hidden text-[11px] text-muted-foreground 2xl:inline">{hint}</span>}
    </Comp>
  );
};
const Sep = () => <span className="mx-0.5 h-3.5 w-px shrink-0 bg-border" aria-hidden />;

/* North arrow + scale bar. Updated imperatively from the scene so orbiting never re-renders the page. In Tomt-modus the
   arrow points to true north (map north rotated by the site bearing); on the grid it points along the scene's -z. */
const Compass = forwardRef(function Compass({ bearing = 0, trueNorth = false }, ref) {
  const [s, setS] = useState({ px: 0, heading: 0 });
  useImperativeHandle(ref, () => ({ set: (px, heading) => setS({ px, heading }) }), []);
  const pxPerM = s.px / 10;
  let metres = 10;
  for (const m of [1, 2, 5, 10, 20, 50, 100]) if (m * pxPerM <= 150) metres = m;
  const w = Math.max(24, Math.min(150, metres * pxPerM));
  const rot = (-s.heading * 180) / Math.PI - (bearing || 0);
  return (
    <div className="pointer-events-none flex items-end gap-3 text-[10.5px] text-muted-foreground" data-testid="rig-compass" data-bearing={Math.round(bearing || 0)} data-true-north={trueNorth ? "1" : "0"}>
      <div className="relative">
        <svg width="30" height="30" viewBox="0 0 30 30" style={{ transform: `rotate(${rot}deg)` }} aria-label={trueNorth ? "Nordpil (sant nord)" : "Nordpil"}>
          <circle cx="15" cy="15" r="13" fill="rgba(255,255,255,0.78)" stroke="#c6cdd6" />
          <path d="M15 4.5 L19 17 L15 14.5 L11 17 Z" fill={trueNorth ? "#266af3" : "#1f2937"} />
          <path d="M15 25.5 L11 13 L15 15.5 L19 13 Z" fill="#c6cdd6" />
        </svg>
        {trueNorth && <span className="absolute -right-1 -top-1 rounded-sm bg-background/90 px-0.5 font-mono text-[8.5px] leading-3 text-primary">N</span>}
      </div>
      {s.px > 0 && (
        <div className="pb-1">
          <div className="h-[5px] border-b border-l border-r border-foreground/60" style={{ width: w }} />
          <div className="mt-0.5 font-mono tabular">{metres} m</div>
        </div>
      )}
    </div>
  );
});

/* ---------------- page ---------------- */

export default function RigDesignPage() {
  const { id } = useParams();
  const ed = useRigEditor(id);
  const desktop = useMediaQuery("(min-width: 1024px)");
  const ws = useWorkspace();
  const [genOpen, setGenOpen] = useState(false);
  const [pendingType, setPendingType] = useState(null);
  const [libOpen, setLibOpen] = useState(false);
  const [inspOpen, setInspOpen] = useState(false);
  const [drawer, setDrawer] = useState(null); // bom | kalkyle | leveranse | helse | null
  const [feedback, setFeedback] = useState(null);
  const [sitePrompt, setSitePrompt] = useState(false); // "where is the site?" – shown when the project has no coordinates
  const [siteStatus, setSiteStatus] = useState(null); // {loaded, failed, total} tile progress from the scene
  const onSiteStatus = useCallback((s) => setSiteStatus(s), []);
  const compassRef = useRef(null);
  const sceneApiRef = useRef(null);
  const onMeasure = useCallback((px, heading) => compassRef.current?.set(px, heading), []);
  const toggleDrawer = useCallback((tab) => setDrawer((d) => (d === tab ? null : tab)), []);
  const openDrawer = useCallback((tab) => setDrawer(tab), []);

  /** «Ta bilde»: clears the selection (no accent outlines in the picture), renders one clean frame and downloads it as PNG. */
  const takePhoto = useCallback(() => {
    const api = sceneApiRef.current;
    if (!api?.capture) { toast.error("Scenen er ikke klar ennå"); return; }
    ed.clearSelection();
    setTimeout(async () => {
      try {
        const url = await api.capture();
        const stamp = new Date().toISOString().slice(0, 10);
        const base = (ed.project?.name || "rigg").replace(/[^\p{L}\p{N} _-]+/gu, "").trim() || "rigg";
        const a = document.createElement("a");
        a.href = url;
        a.download = `${base} - riggdesign ${stamp}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setFeedback("Bilde lastet ned");
      } catch (e) {
        toast.error("Kunne ikke ta bilde av scenen");
      }
    }, 60);
  }, [ed]);

  /** Grid | Tomt. Enabling without coordinates opens the address prompt instead of silently doing nothing. */
  const setGround = useCallback((v) => {
    if (v === "grid") { ed.enableSite(false); setSitePrompt(false); return; }
    if (ed.enableSite(true)) { setSitePrompt(false); setFeedback(ed.site?.address ? `Tomt vist – ${ed.site.address}` : ed.project?.address ? `Tomt vist – ${ed.project.address}` : "Tomt vist"); }
    else setSitePrompt(true);
  }, [ed]);

  // transient feedback chip (placements)
  useEffect(() => {
    if (!feedback) return;
    const t = setTimeout(() => setFeedback(null), 2200);
    return () => clearTimeout(t);
  }, [feedback]);

  // keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target?.tagName;
      if (["INPUT", "TEXTAREA", "SELECT"].includes(tag) || e.target?.isContentEditable) return;
      if (e.target?.closest?.("[role=dialog],[role=menu]")) return;
      const mod = e.metaKey || e.ctrlKey;
      if (e.key === "Escape") {
        if (pendingType) setPendingType(null);
        else if (ed.siteTool) ed.setSiteTool(null);
        else if (sitePrompt) setSitePrompt(false);
        else if (genOpen) setGenOpen(false);
        else if (drawer) setDrawer(null);
        else if (ed.selection.size) ed.clearSelection();
        else if (ws.fullscreen) workspace.setFullscreen(false);
        return;
      }
      if (!mod && (e.key === "f" || e.key === "F")) { e.preventDefault(); ed.fitView(undefined, ed.selectedIds); return; }
      if (!ed.canEdit || ed.mode !== "design") return;
      if (!mod && (e.key === "m" || e.key === "M") && ed.siteEnabled) { e.preventDefault(); ed.setSiteTool((t) => (t === "move" ? null : "move")); return; }
      if (mod && e.key.toLowerCase() === "z") { e.preventDefault(); e.shiftKey ? ed.redo() : ed.undo(); return; }
      if (mod && e.key.toLowerCase() === "y") { e.preventDefault(); ed.redo(); return; }
      if (mod && e.key.toLowerCase() === "d") { e.preventDefault(); ed.duplicateSelected(); return; }
      if (mod && e.key.toLowerCase() === "s") { e.preventDefault(); ed.save(); return; }
      if ((e.key === "Delete" || e.key === "Backspace") && ed.selection.size) { e.preventDefault(); ed.removeSelected(); return; }
      if ((e.key === "r" || e.key === "R") && ed.selection.size && !pendingType) { ed.rotateSelected(90); return; }
      const step = e.shiftKey ? ed.grid * 5 : ed.grid;
      if (e.key === "ArrowLeft" && ed.selection.size) { e.preventDefault(); ed.moveSelectedBy(-step, 0); }
      if (e.key === "ArrowRight" && ed.selection.size) { e.preventDefault(); ed.moveSelectedBy(step, 0); }
      if (e.key === "ArrowUp" && ed.selection.size) { e.preventDefault(); ed.moveSelectedBy(0, -step); }
      if (e.key === "ArrowDown" && ed.selection.size) { e.preventDefault(); ed.moveSelectedBy(0, step); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ed, pendingType, genOpen, drawer, ws.fullscreen, sitePrompt]);

  useEffect(() => { if (ed.mode === "delivery") setPendingType(null); }, [ed.mode]);
  useEffect(() => { if (ed.siteTool) setPendingType(null); }, [ed.siteTool]);
  useEffect(() => { if (ed.siteEnabled) setSitePrompt(false); }, [ed.siteEnabled]);
  useEffect(() => {
    if (!ed.dirty) return;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [ed.dirty]);

  if (ed.loading) return <div className="flex h-screen items-center justify-center" data-testid="rig-design-page"><LoadingState text="Laster riggdesign…" /></div>;
  if (ed.error) return <div className="h-screen p-8" data-testid="rig-design-page"><ErrorState error={ed.error} onRetry={ed.refetch} /></div>;

  const { project, summary, estimate, conflicts, mode, view, activeFloor, floors, health } = ed;
  const kpi = summary.kpi;
  const target = project?.capacity_persons || 0;
  const editable = ed.canEdit && mode === "design";
  const floorOptions = [{ value: "all", label: "Alle" }, ...Array.from({ length: floors }).map((_, i) => ({ value: i, label: `${i + 1}` }))];
  const pendingT = pendingType ? ed.types[pendingType] : null;
  const deliveryCounts = ed.delivery?.counts || {};
  const tone = healthTone(health);
  const bottomOffset = drawer && desktop ? DRAWER_H + 12 : 12;
  const siteOn = ed.siteEnabled;
  const tilesLoading = siteOn && siteStatus && siteStatus.total > 0 && siteStatus.loaded + siteStatus.failed < siteStatus.total;
  const tilesMissing = siteOn && siteStatus && siteStatus.total > 0 && !tilesLoading && siteStatus.failed > 0;
  const groundOptions = [
    { value: "grid", label: <SegIconLabel icon={Grid3x3}>Grid</SegIconLabel>, title: "Abstrakt rutenett" },
    { value: "site", label: <SegIconLabel icon={Map}>Tomt</SegIconLabel>, title: "Ekte tomt – satellitt eller kart under riggen", disabled: !ed.siteConfig },
  ];

  const pick = (typeId) => { setPendingType(typeId); if (!desktop) setLibOpen(false); };
  const addNow = (typeId) => { ed.addModule(typeId); setPendingType(null); if (!desktop) setLibOpen(false); };

  const DesignStats = () => (
    <>
      <Stat label="Moduler" value={kpi.modules} hint={floors > 1 ? Object.entries(kpi.per_floor).map(([f, n]) => `E${Number(f) + 1} ${n}`).join(" · ") : null} onClick={() => toggleDrawer("bom")} testId="rig-kpi-modules" />
      <Sep />
      <Stat label="Senger" value={target ? `${kpi.beds} / ${target}` : kpi.beds} hint={target ? (kpi.beds >= target ? "behov dekket" : `${target - kpi.beds} mangler`) : null} tone={target && kpi.beds < target ? "warn" : undefined} onClick={() => toggleDrawer("bom")} testId="rig-kpi-beds" />
      <Sep />
      <Stat label="Areal" value={`${num(kpi.area_m2)} m²`} hint={`${num(kpi.weight_t, 1)} t`} onClick={() => toggleDrawer("bom")} testId="rig-kpi-area" />
      <Sep />
      <Stat label="Transporter" value={kpi.transports} hint={kpi.transports ? `≈ ${kpi.delivery_days} dager` : null} onClick={() => toggleDrawer("bom")} testId="rig-kpi-transports" />
      <Sep />
      <Stat label="Montasje" value={`${num(kpi.assembly_hours)} t`} onClick={() => toggleDrawer("bom")} testId="rig-kpi-assembly" />
      <Sep />
      <Stat label="Estimat" value={estimate ? mnok(estimate.revenue) : kpi.modules ? "…" : "–"} hint={estimate ? `DG ${num(estimate.margin_pct, 0)} % · ${mnok(estimate.monthly_total)}/mnd` : null} onClick={() => toggleDrawer("kalkyle")} testId="rig-kpi-value" />
    </>
  );
  const DeliveryStats = () => {
    const c = deliveryCounts;
    const sum = (...ks) => ks.reduce((s, k) => s + (c[k] || 0), 0);
    return (
      <>
        <Stat label="Moduler" value={kpi.modules} onClick={() => toggleDrawer("leveranse")} testId="rig-kpi-modules" />
        <Sep />
        <Stat label="Montert" value={`${ed.delivery?.installed ?? 0} / ${ed.delivery?.total ?? kpi.modules}`} hint={`${ed.delivery?.progress_pct ?? 0} %`} onClick={() => toggleDrawer("leveranse")} testId="rig-kpi-installed" />
        <Sep />
        <Stat label="På vei" value={sum("pa_vei", "klar_for_transport")} onClick={() => toggleDrawer("leveranse")} testId="rig-kpi-transit" />
        <Sep />
        <Stat label="Klargjøres" value={sum("klargjoring", "reservert", "pa_lager")} onClick={() => toggleDrawer("leveranse")} testId="rig-kpi-preparing" />
        <Sep />
        <Stat label="Uten enhet" value={sum("ikke_tildelt")} tone={sum("ikke_tildelt") ? "warn" : undefined} onClick={() => toggleDrawer("leveranse")} testId="rig-kpi-unassigned" />
        <Sep />
        <Stat label="Senger" value={target ? `${kpi.beds} / ${target}` : kpi.beds} testId="rig-kpi-beds" />
      </>
    );
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex h-screen flex-col overflow-hidden bg-background" data-testid="rig-design-page">
        {/* ---- work header ---- */}
        <header className="grid h-12 shrink-0 grid-cols-[1fr_auto_1fr] items-center gap-2 border-b border-border/70 px-2 sm:px-3" data-testid="rig-header">
          <div className="flex min-w-0 items-center gap-1">
            <button type="button" onClick={() => workspace.openMobileNav()} className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground lg:hidden" aria-label="Meny" data-testid="rig-mobile-menu"><Menu className="h-4 w-4" strokeWidth={1.75} /></button>
            <Link to={`/prosjekter/${id}`} className="inline-flex h-8 min-w-0 items-center gap-1 rounded-md pl-1 pr-2 text-[13px] text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:bg-muted" data-testid="rig-back-to-project" title="Tilbake til prosjektet">
              <ChevronLeft className="h-4 w-4 shrink-0" strokeWidth={1.75} />
              <span className="truncate">{project?.name}</span>
            </Link>
            <span className="hidden text-muted-foreground/50 sm:inline" aria-hidden>/</span>
            <h1 className="hidden truncate text-[13px] font-medium sm:block" data-testid="rig-title">Riggdesign</h1>
          </div>
          <Seg className="bg-muted/60" value={mode} onChange={ed.setMode} options={[{ value: "design", label: "Design" }, { value: "delivery", label: "Levering" }]} testId="rig-mode" />
          <div className="flex items-center justify-end gap-1.5">
            <SaveStatus ed={ed} className="hidden sm:inline-flex" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50" aria-label="Flere valg" data-testid="rig-more-menu"><MoreHorizontal className="h-4 w-4" strokeWidth={1.75} /></button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuItem onClick={() => ed.fitView("iso")} data-testid="rig-menu-fit"><Maximize2 className="mr-2 h-4 w-4" /> Tilpass visning <DropdownMenuShortcut>F</DropdownMenuShortcut></DropdownMenuItem>
                <DropdownMenuItem onClick={takePhoto} data-testid="rig-menu-photo"><Camera className="mr-2 h-4 w-4" /> Ta bilde (PNG)</DropdownMenuItem>
                <DropdownMenuCheckboxItem checked={ed.showLabels} onCheckedChange={ed.setShowLabels} data-testid="rig-menu-labels">Vis etiketter på alle moduler</DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem checked={ed.snapEnabled} onCheckedChange={ed.setSnapEnabled} data-testid="rig-menu-snap">Magnetisk snapping</DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem checked={ws.fullscreen} onCheckedChange={(v) => workspace.setFullscreen(!!v)} data-testid="rig-menu-fullscreen">Fullskjerm</DropdownMenuCheckboxItem>
                <DropdownMenuSeparator />
                {ed.canEdit && <DropdownMenuItem onClick={ed.save} disabled={!ed.dirty || ed.saving} data-testid="rig-menu-save"><Save className="mr-2 h-4 w-4" /> Lagre nå <DropdownMenuShortcut>Ctrl+S</DropdownMenuShortcut></DropdownMenuItem>}
                <DropdownMenuItem onClick={ed.refetch} data-testid="rig-menu-reload"><RefreshCw className="mr-2 h-4 w-4" /> Last inn på nytt</DropdownMenuItem>
                <DropdownMenuSeparator />
                {DRAWER_TABS.map((t) => <DropdownMenuItem key={t.id} onClick={() => openDrawer(t.id)} data-testid={`rig-menu-drawer-${t.id}`}><t.icon className="mr-2 h-4 w-4" /> {t.label}</DropdownMenuItem>)}
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild><Link to={`/prosjekter/${id}`}><ArrowUpRight className="mr-2 h-4 w-4" /> Åpne prosjektet</Link></DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {ed.canEdit && (
              <RigGeneratePopover ed={ed} open={genOpen} onOpenChange={setGenOpen}>
                <Button size="sm" className="h-8" data-testid="rig-generate-button"><Wand2 /> <span className="hidden sm:inline">Generer riggforslag</span><span className="sm:hidden">Generer</span></Button>
              </RigGeneratePopover>
            )}
          </div>
        </header>

        {/* ---- one-line design summary ---- */}
        <div className="flex h-9 shrink-0 items-center overflow-x-auto px-2 [&::-webkit-scrollbar]:hidden sm:px-3" data-testid="rig-kpi">
          {mode === "delivery" ? <DeliveryStats /> : <DesignStats />}
        </div>

        {/* ---- workspace ---- */}
        <div className="flex min-h-0 flex-1">
          {desktop && (mode === "design"
            ? <ModuleLibrary library={ed.library} loading={!ed.library} pendingType={pendingType} onPick={pick} onAdd={addNow} disabled={!editable} className="w-[272px] shrink-0" />
            : <aside className="w-[300px] shrink-0 overflow-hidden bg-background" data-testid="rig-delivery-side"><DeliveryPanel ed={ed} compact /></aside>
          )}

          <div className="relative min-w-0 flex-1" style={{ background: SCENE_BG }} data-testid="rig-canvas-column">
            <RigScene editor={ed} pendingType={editable ? pendingType : null} onPlaced={() => setPendingType(null)} onFeedback={setFeedback} onMeasure={onMeasure} onSiteStatus={onSiteStatus} sceneApi={sceneApiRef} />

            {/* top-left: ground (Grid | Tomt) + which floor is shown */}
            <div className="absolute left-3 top-3 flex max-w-[calc(100%-7.5rem)] flex-wrap items-start gap-2" data-testid="rig-ground-controls">
              <Glass><Seg prefix="Visning" value={siteOn ? "site" : "grid"} onChange={setGround} options={groundOptions} testId="rig-ground" /></Glass>
              {floors > 1 && <Glass><Seg prefix="Etasje" value={activeFloor} onChange={ed.setActiveFloor} options={floorOptions} testId="rig-floor" /></Glass>}
            </div>

            {/* top-right: view controls (below the view cube in 3D) */}
            <Glass className="absolute right-3 flex flex-col gap-0.5 p-0.5" style={{ top: view === "3D" ? 118 : 12 }} testId="rig-view-controls">
              <ToolButton icon={view === "3D" ? Square : Box} label={view === "3D" ? "Planvisning (2D)" : "Perspektiv (3D)"} onClick={() => ed.setView(view === "3D" ? "2D" : "3D")} side="left" testId="rig-view-toggle" />
              <ToolButton icon={Maximize2} label="Tilpass visning" shortcut="F" onClick={() => ed.fitView("iso")} side="left" testId="rig-tool-fit" />
              <ToolButton icon={Tag} label={ed.showLabels ? "Skjul etiketter" : "Vis etiketter"} active={ed.showLabels} onClick={() => ed.setShowLabels(!ed.showLabels)} side="left" testId="rig-tool-labels" />
              <ToolButton icon={Camera} label="Ta bilde (PNG)" onClick={takePhoto} side="left" testId="rig-tool-photo" />
              {desktop && <ToolButton icon={ws.fullscreen ? Minimize : Maximize} label={ws.fullscreen ? "Avslutt fullskjerm" : "Fullskjerm"} shortcut={ws.fullscreen ? "Esc" : undefined} onClick={() => workspace.setFullscreen(!ws.fullscreen)} side="left" testId="rig-tool-fullscreen" />}
            </Glass>

            {/* bottom-centre: editing tools */}
            {editable && desktop && (
              <Glass className="absolute left-1/2 flex -translate-x-1/2 items-center gap-0.5 p-0.5" style={{ bottom: bottomOffset }} testId="rig-toolbar">
                <ToolButton icon={Undo2} label="Angre" shortcut="Ctrl+Z" onClick={ed.undo} disabled={!ed.canUndo} side="top" testId="rig-tool-undo" />
                <ToolButton icon={Redo2} label="Gjør om" shortcut="Ctrl+⇧Z" onClick={ed.redo} disabled={!ed.canRedo} side="top" testId="rig-tool-redo" />
                <span className="mx-0.5 h-4 w-px bg-border" aria-hidden />
                <ToolButton icon={RotateCw} label="Roter 90°" shortcut="R" onClick={() => ed.rotateSelected(90)} disabled={!ed.selection.size} side="top" testId="rig-tool-rotate" />
                <ToolButton icon={Copy} label="Dupliser" shortcut="Ctrl+D" onClick={ed.duplicateSelected} disabled={!ed.selection.size} side="top" testId="rig-tool-duplicate" />
                <span className="mx-0.5 h-4 w-px bg-border" aria-hidden />
                <ToolButton icon={Magnet} label={ed.snapEnabled ? "Snapping på" : "Snapping av"} active={ed.snapEnabled} onClick={() => ed.setSnapEnabled(!ed.snapEnabled)} side="top" testId="rig-tool-snap" />
                {siteOn && (
                  <>
                    <span className="mx-0.5 h-4 w-px bg-border" aria-hidden />
                    <ToolButton icon={Move} label={ed.siteTool === "move" ? "Avslutt flytting av tomt" : "Flytt tomt under riggen"} shortcut="M" active={ed.siteTool === "move"} onClick={() => ed.setSiteTool((t) => (t === "move" ? null : "move"))} side="top" testId="rig-tool-site-move" />
                  </>
                )}
              </Glass>
            )}

            {/* bottom-left: orientation + scale */}
            {desktop && <div className="absolute left-3 transition-[bottom] duration-200" style={{ bottom: bottomOffset }}><Compass ref={compassRef} bearing={siteOn ? ed.site.bearing_deg || 0 : 0} trueNorth={siteOn} /></div>}

            {/* bottom-right: map layer + attribution (Tomt-modus, design) – otherwise attribution lives in the status bar */}
            {siteOn && desktop && mode === "design" && <SiteLayerControl ed={ed} className="absolute right-3 transition-[bottom] duration-200" style={{ bottom: bottomOffset }} />}

            {/* top-centre: placement banner / site tool hint / address prompt / feedback */}
            {sitePrompt && !siteOn ? (
              <div className="absolute left-1/2 top-3 -translate-x-1/2" data-testid="rig-site-prompt-wrap"><SitePrompt ed={ed} onClose={(r) => { setSitePrompt(false); if (r) setFeedback(`Tomt plassert – ${r.formatted_address || "adresse funnet"}`); }} /></div>
            ) : pendingT && editable ? (
              <Glass className="absolute left-1/2 top-3 -translate-x-1/2 px-3 py-1.5 text-[12.5px]" testId="rig-placement-banner">
                Klikk i scenen for å plassere <span className="font-mono font-medium">{pendingT.code}</span> på etasje {(activeFloor === "all" ? 0 : activeFloor) + 1} · <kbd className="kbd">R</kbd> roterer · <kbd className="kbd">Shift</kbd> plasserer flere
                <button type="button" onClick={() => setPendingType(null)} className="ml-2 inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Avbryt plassering" data-testid="rig-placement-cancel"><X className="h-3.5 w-3.5" /></button>
              </Glass>
            ) : ed.siteTool === "move" && editable ? (
              <Glass className="absolute left-1/2 top-3 -translate-x-1/2 px-3 py-1.5 text-[12.5px]" testId="rig-site-move-banner">
                <Move className="mr-1.5 inline h-3.5 w-3.5 align-[-2px] text-primary" strokeWidth={1.75} /> Dra i kartet for å skyve tomten under riggen · <kbd className="kbd">Esc</kbd> avslutter
                <button type="button" onClick={() => ed.setSiteTool(null)} className="ml-2 inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Avslutt flytting" data-testid="rig-site-move-cancel"><X className="h-3.5 w-3.5" /></button>
              </Glass>
            ) : feedback ? (
              <Glass className="absolute left-1/2 top-3 -translate-x-1/2 px-3 py-1.5 text-[12px] text-foreground/90" testId="rig-feedback"><span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-primary align-middle" aria-hidden />{feedback}</Glass>
            ) : null}

            {/* empty rig */}
            {ed.modules.length === 0 && (
              <div className="absolute left-1/2 top-1/2 w-[340px] -translate-x-1/2 -translate-y-1/2" data-testid="rig-empty-hint">
                <Glass className="px-5 py-4 text-[12.5px] leading-5">
                  <div className="text-[13px] font-medium">Tom rigg</div>
                  <p className="mt-0.5 text-muted-foreground">Generer et riggforslag fra prosjektets behov{target ? ` (${target} personer)` : ""}, eller dra moduler inn fra biblioteket.</p>
                  {ed.canEdit && <Button size="sm" className="mt-3" onClick={() => setGenOpen(true)} data-testid="rig-empty-generate"><Wand2 /> Generer riggforslag</Button>}
                </Glass>
              </div>
            )}

            {/* delivery legend */}
            {mode === "delivery" && (
              <Glass className="absolute right-3 px-3 py-2" style={{ bottom: bottomOffset }} testId="rig-delivery-legend">
                <div className="mb-1 flex items-baseline justify-between gap-4 text-[11px] text-muted-foreground"><span className="font-medium">Leveransestatus</span><span className="tabular">{ed.delivery?.installed ?? 0}/{ed.delivery?.total ?? ed.modules.length} montert</span></div>
                <ul className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                  {DELIVERY_ORDER.filter((s) => deliveryCounts[s]).map((s) => (
                    <li key={s} className="flex items-center justify-between gap-3 text-[11.5px]"><span className="inline-flex items-center gap-1.5"><Dot color={DELIVERY_COLORS[s]} />{DELIVERY_LABELS[s]}</span><span className="tabular text-muted-foreground">{deliveryCounts[s]}</span></li>
                  ))}
                </ul>
              </Glass>
            )}

            {/* mobile: attribution above the bottom bar */}
            {siteOn && !desktop && <div className="absolute right-3" style={{ bottom: 52 }}><SiteAttribution layer={ed.siteLayer} /></div>}

            {/* mobile bottom bar */}
            {!desktop && (
              <div className="absolute inset-x-3 flex items-center justify-between gap-2" style={{ bottom: 12 }}>
                <Button variant="outline" size="sm" onClick={() => setLibOpen(true)} disabled={!editable} data-testid="rig-mobile-library"><Boxes /> Bibliotek</Button>
                <Button variant="outline" size="sm" onClick={() => setDrawer(drawer || "bom")} data-testid="rig-mobile-drawer"><PanelBottom /> Paneler</Button>
                <Button variant="outline" size="sm" onClick={() => setInspOpen(true)} data-testid="rig-mobile-inspector"><SlidersHorizontal /> Egenskaper</Button>
              </div>
            )}

            {desktop && drawer && <RigDrawer ed={ed} tab={drawer} onTab={setDrawer} onClose={() => setDrawer(null)} height={DRAWER_H} />}
          </div>

          {desktop && <RigInspector ed={ed} className="w-[336px] shrink-0" onOpenDrawer={openDrawer} />}
        </div>

        {/* ---- status bar ---- */}
        <div className="flex h-8 shrink-0 items-center gap-1 overflow-x-auto whitespace-nowrap border-t border-border/70 px-2 text-[11.5px] text-muted-foreground [&::-webkit-scrollbar]:hidden sm:px-3" data-testid="rig-statusbar">
          <span className="hidden tabular sm:inline">Rutenett {num(ed.grid, 1)} m</span>
          <Sep />
          <button type="button" onClick={() => ed.setSnapEnabled(!ed.snapEnabled)} className="inline-flex h-6 items-center gap-1 rounded px-1.5 hover:bg-muted hover:text-foreground" aria-pressed={ed.snapEnabled} data-testid="rig-status-snap"><Magnet className="h-3 w-3" /> Snap {ed.snapEnabled ? "på" : "av"}</button>
          <Sep />
          <span className="hidden sm:inline" data-testid="rig-status-floor">{activeFloor === "all" ? `Alle ${floors} etasje${floors > 1 ? "r" : ""}` : `Etasje ${activeFloor + 1} av ${floors}`}</span>
          {siteOn && (
            <>
              <Sep />
              <span className="inline-flex items-center gap-1.5" data-testid="rig-status-site">
                <MapPin className="h-3 w-3 text-primary" strokeWidth={1.75} />
                {tilesLoading ? <span className="inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Laster kart {siteStatus.loaded + siteStatus.failed}/{siteStatus.total}</span>
                  : tilesMissing ? <span className="text-amber-700" title="Kartkilden mangler bilder for deler av området">{siteStatus.failed} kartfliser mangler</span>
                  : <span className="hidden font-mono tabular xl:inline" title="Riggens senter">{ed.rigCenterLatLng ? fmtLatLng(ed.rigCenterLatLng.lat, ed.rigCenterLatLng.lng) : "Tomt"}</span>}
                {desktop && mode !== "design" && <SiteAttribution layer={ed.siteLayer} className="bg-transparent px-0 backdrop-blur-0" />}
              </span>
            </>
          )}
          {ed.selection.size > 0 && <><Sep /><span className="tabular" data-testid="rig-status-selection">{ed.selection.size} valgt</span></>}
          <Sep />
          <button type="button" onClick={() => toggleDrawer("helse")} className={cn("inline-flex h-6 items-center gap-1.5 rounded px-1.5 hover:bg-muted", tone === "ok" ? "hover:text-foreground" : "text-foreground")} data-testid="rig-status-health" aria-pressed={drawer === "helse"}>
            <span className={cn("h-1.5 w-1.5 rounded-full", LEVEL_DOT[tone])} aria-hidden /> {healthText(health)}
            {conflicts.size > 0 && mode === "design" && <span className="text-muted-foreground" data-testid="rig-conflict-chip">· {conflicts.size} overlapper</span>}
          </button>
          <SaveStatus ed={ed} className="ml-auto sm:hidden" />
          <div className="ml-auto hidden items-center gap-0.5 lg:flex" data-testid="rig-drawer-tabs">
            {DRAWER_TABS.map((t) => (
              <button key={t.id} type="button" onClick={() => toggleDrawer(t.id)} aria-pressed={drawer === t.id} data-testid={`rig-tab-${t.id}`}
                className={cn("inline-flex h-6 items-center gap-1.5 rounded px-2 transition-colors duration-150 hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:bg-muted", drawer === t.id && "bg-muted text-foreground")}>
                {t.id === "helse" ? <HeartPulse className="h-3 w-3" /> : <t.icon className="h-3 w-3" />} {t.label}
              </button>
            ))}
          </div>
        </div>

        {!desktop && (
          <>
            <Sheet open={libOpen} onOpenChange={setLibOpen}><SheetContent side="left" className="w-[300px] p-0"><SheetTitle className="sr-only">Modulbibliotek</SheetTitle><ModuleLibrary library={ed.library} loading={!ed.library} pendingType={pendingType} onPick={pick} onAdd={addNow} disabled={!editable} /></SheetContent></Sheet>
            <Sheet open={inspOpen} onOpenChange={setInspOpen}><SheetContent side="right" className="w-[92vw] max-w-[360px] p-0"><SheetTitle className="sr-only">Egenskaper</SheetTitle><RigInspector ed={ed} onOpenDrawer={(t) => { setInspOpen(false); setDrawer(t); }} /></SheetContent></Sheet>
            <Sheet open={!!drawer} onOpenChange={(o) => { if (!o) setDrawer(null); }}><SheetContent side="bottom" className="h-[72vh] p-0"><SheetTitle className="sr-only">Paneler</SheetTitle><div className="relative h-full"><RigDrawer ed={ed} tab={drawer || "bom"} onTab={setDrawer} onClose={() => setDrawer(null)} className="static h-full border-t-0 shadow-none" height="100%" /></div></SheetContent></Sheet>
          </>
        )}
      </div>
    </TooltipProvider>
  );
}
